import { useState } from 'react';
import Drawer from '../Drawer/Drawer';
import StatusBadge from '../StatusBadge/StatusBadge';
import { updateLoad, updateBid, notifyTransporters, sendNotification } from '../../lib/directus';
import styles from './LoadDrawer.module.css';

export default function LoadDrawer({ load, onClose, onAwarded }) {
  const [selectedBid, setSelectedBid] = useState(null);
  const [awarding, setAwarding]       = useState(false);
  const [posting, setPosting]         = useState(false);
  const [awardError, setAwardError]   = useState('');

  const isOpen   = !!load;
  const isPending = load && load.status === 'Pending';
  const showBids  = load && load.status === 'Open';

  async function handlePost() {
    setPosting(true);
    setAwardError('');
    try {
      await updateLoad(load._id, { bidding_status: 'Open' });
      notifyTransporters(
        'New load available for bidding',
        `A new load is available: ${load.cargo || 'cargo'} from ${load.pickup} to ${load.delivery}. Log in to submit your bid.`
      ).catch(console.error);
      onAwarded && onAwarded();
      handleClose();
    } catch (err) {
      setAwardError(err.message);
    } finally {
      setPosting(false);
    }
  }

  async function handleAward() {
    if (!selectedBid?.transporterId) return;
    setAwarding(true);
    setAwardError('');
    try {
      await updateLoad(load._id, { awarded_transporter: selectedBid.transporterId, bidding_status: 'awarded' });
      if (selectedBid.bidId) {
        await updateBid(selectedBid.bidId, { status: 'Won' });
      }
      if (selectedBid.transporterUserId) {
        sendNotification(
          selectedBid.transporterUserId,
          'Bid awarded — you won!',
          `Congratulations! Your bid of ${selectedBid.price} for the load from ${load.pickup} to ${load.delivery} (${load.cargo}) has been awarded. Please assign a truck and driver to proceed.`
        ).catch(console.error);
      }
      onAwarded && onAwarded();
      handleClose();
    } catch (err) {
      setAwardError(err.message);
    } finally {
      setAwarding(false);
    }
  }

  function handleClose() {
    setSelectedBid(null);
    setAwardError('');
    onClose();
  }

  const footer = isPending ? (
    <button className={styles.btnAward} onClick={handlePost} disabled={posting}>
      {posting ? 'Posting...' : 'Post for bidding'}
    </button>
  ) : showBids && selectedBid ? (
    <button className={styles.btnAward} onClick={handleAward} disabled={awarding}>
      {awarding ? 'Awarding...' : `Award to ${selectedBid.transporter}`}
    </button>
  ) : null;

  return (
    <Drawer
      isOpen={isOpen}
      title="Load details"
      onClose={handleClose}
      footer={footer}
    >
      {load && (
        <>
          {/* Load info card */}
          <div className={styles.card}>
            <p className={styles.cardTitle}>Load information</p>
            <div className={styles.grid}>
              <div>
                <p className={styles.fieldLabel}>Pickup</p>
                <p className={styles.fieldValue}>{load.pickup}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Delivery</p>
                <p className={styles.fieldValue}>{load.delivery}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Cargo</p>
                <p className={styles.fieldValue}>{load.cargo}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Distance</p>
                <p className={styles.fieldValue}>{load.distance}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Pickup date</p>
                <p className={styles.fieldValue}>{load.pickupDate}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Suggested rate</p>
                <p className={styles.fieldValue}>{load.suggestedRate}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Estimated duration</p>
                <p className={styles.fieldValue}>{load.estimatedDuration}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Status</p>
                <StatusBadge status={load.status} />
              </div>
            </div>
          </div>

          {/* Parties involved */}
          <div className={styles.card}>
            <p className={styles.cardTitle}>Parties involved</p>
            <div className={styles.grid}>
              <div>
                <p className={styles.fieldLabel}>Seller</p>
                <p className={styles.fieldValue}>{load.seller}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Buyer</p>
                <p className={styles.fieldValue}>{load.buyer}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Transporter</p>
                {load.transporter ? (
                  <p className={styles.fieldValue}>{load.transporter}</p>
                ) : (
                  <p className={styles.pendingText}>Pending selection...</p>
                )}
              </div>
            </div>
          </div>

          {/* Submitted bids — only for Pending/Open */}
          {showBids && load.bids && load.bids.length > 0 && (
            <div className={styles.card}>
              <p className={styles.cardTitle}>Submitted bids</p>
              {load.bids.map((bid, i) => (
                <label key={i} className={styles.bidRow}>
                  <input
                    type="radio"
                    name="bid"
                    className={styles.radio}
                    checked={selectedBid?.transporter === bid.transporter}
                    onChange={() => { setSelectedBid(bid); setAwardError(''); }}
                  />
                  <div className={styles.bidInfo}>
                    <span className={styles.bidTransporter}>{bid.transporter}</span>
                    <span className={styles.bidTrips}>{bid.trips} trips</span>
                  </div>
                  <span className={styles.bidPrice}>{bid.price}</span>
                </label>
              ))}
              {awardError && <p className={styles.awardError}>{awardError}</p>}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
